import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  DialogTrigger,
  View,
} from '@adobe/react-spectrum';

export interface ConfirmDialogProps {
  /** Whether the dialog is open. */
  isOpen: boolean;
  /** Called when the dialog open state changes (e.g. backdrop click). */
  onOpenChange: (open: boolean) => void;
  /** Dialog title. */
  title: string;
  /** Dialog body content. */
  children: React.ReactNode;
  /** Label for the primary (confirm) button. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Label for the cancel button. Defaults to "Cancel". */
  cancelLabel?: string;
  /** AlertDialog variant. Defaults to "destructive". */
  variant?: 'confirmation' | 'information' | 'destructive' | 'error';
  /** Called when the user confirms. */
  onConfirm: () => void;
  /** Called when the user cancels. Defaults to closing via onOpenChange(false). */
  onCancel?: () => void;
}

/**
 * Thin wrapper around Spectrum AlertDialog that hides the
 * `<View UNSAFE_style={{ display: 'none' }} />` boilerplate.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onOpenChange,
  title,
  children,
  confirmLabel,
  cancelLabel,
  variant = 'destructive',
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <DialogTrigger
      isOpen
      onOpenChange={(open) => {
        if (!open) onOpenChange(false);
      }}
    >
      <View UNSAFE_style={{ display: 'none' }} />
      <AlertDialog
        variant={variant}
        title={title}
        primaryActionLabel={confirmLabel ?? t('components.confirmDialog.confirm')}
        cancelLabel={cancelLabel ?? t('primitives.cancel')}
        onPrimaryAction={onConfirm}
        onCancel={onCancel ?? (() => onOpenChange(false))}
      >
        {children}
      </AlertDialog>
    </DialogTrigger>
  );
};
